import * as React from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  TUnion,
  TEmptyRecord,
  EUnion,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {Select, Option} from '@packages/systems/design-system/WebflowSelect';
import {LogicLabel} from '@packages/systems/flows/shared';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {emotionStyled} from '@packages/utilities/styled';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {useDesignerSelector} from '@packages/systems/designer/flux';

import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';

const itemStatusType = TUnion({
  draft: TEmptyRecord,
  publish: TEmptyRecord,
  staged: TEmptyRecord,
});

const itemStatusOptions = [
  {id: 'draft', label: 'Draft'},
  {id: 'publish', label: 'Live item'},
  {id: 'staged', label: 'Staged for publish'},
];

const selectLastPublished = (state: any) => state.SiteDataStore.lastPublished;

const OptionLabel = emotionStyled('div')({
  pointerEvents: 'all',
  cursor: 'not-allowed',
});

const TooltipWrapper = emotionStyled('div')({
  width: rightSidebarWidth,
});

/**
 * Union type for choosing the status of an item for Create CMS item
 */
export const ItemStatusDropdown = TypeImplementation.Alias({
  type: itemStatusType,
  render: function RenderItemStatus({expr, dispatch}) {
    const strippedExpr = stripAnn(expr);
    const union = ContextualExpressions.getUnion(strippedExpr);
    const initialTag = union ? union.tag : 'draft';
    const [status, setStatus] = React.useState(initialTag);
    const isInitialMount = React.useRef(true);
    const lastPublished = useDesignerSelector(selectLastPublished);

    // set the value to draft if it's a hole
    React.useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        if (ContextualExpressions.isHole(strippedExpr)) {
          dispatch(
            updateExpressionAction(
              EUnion({tag: initialTag, value: ERecord({})})
            )
          );
        }
      }
    }, [dispatch, initialTag, strippedExpr]);

    const handleItemChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type. | TS7006 - Parameter 'meta' implicitly has an 'any' type.
      (id, meta) => {
        if (meta.changeType === 'changeEnd') {
          // prevent changing to "live" if the site has not been published
          if (id === 'publish' && !lastPublished) return;

          setStatus(id);
          dispatch(
            updateExpressionAction(EUnion({tag: id, value: ERecord({})}))
          );
        }
      },
      [dispatch, lastPublished]
    );

    return (
      <>
        <LogicLabel label="Create as" htmlFor="flow-item-status" required />
        <Select
          id="flow-item-status"
          data-automation-id="flow-item-status"
          onChange={handleItemChange}
          value={status}
          aria-label={`Create as: ${status} required`}
        >
          {itemStatusOptions.map(({id, label}) => {
            // If the site is not published, users can't create a published item.
            // So this option should be disabled.
            if (id === 'publish' && !lastPublished) {
              return (
                <Option
                  disabled
                  key={id}
                  value={id}
                  label={
                    <TooltipTrigger
                      placement="left"
                      alternate
                      content={
                        <TooltipWrapper>
                          To create this as a live CMS item, first publish your
                          site.
                        </TooltipWrapper>
                      }
                    >
                      <OptionLabel>{label}</OptionLabel>
                    </TooltipTrigger>
                  }
                />
              );
            } else {
              return <Option key={id} value={id} label={label} />;
            }
          })}
        </Select>
      </>
    );
  },
});
